import {
  Button,
  Card,
  Dropdown,
  DropdownItem,
  Flag,
  Label,
  Separator,
  Textarea,
} from 'platform/components';
import {Box, HStack, Show, Space} from 'platform/foundation';
import {match} from 'ts-pattern';

import {Fragment, useState} from 'react';

import {always, groupBy, isNotNil, toPairs} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {AllOrNone, Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {useTenant} from '../../hooks/useTenant';
import {getLocaleLabel} from '../../utils/getLocaleLabel';
import {PredefinedNotes} from '../PredefinedNotes/PredefinedNotes';

const OTHERS_LOCALE_KEY = '__others__';

export type TemplateBoxTemplate = {
  id: string;
  title: string;
  locale?: string | null;
  [key: string]: unknown;
};

type DocumentTemplateBoxProps = {
  title: string;
  type?: string | Nullish;
  isSelected?: boolean;
  selectedTemplate?: TemplateBoxTemplate | Nullish;
  templates?: TemplateBoxTemplate[] | Nullish;
  authorizationTemplates?: TemplateBoxTemplate[] | Nullish;
  authorizationProfileLabel?: string | Nullish;
  preferredLocale?: string | Nullish;
  onSelect: (isSelected: boolean) => void;
  onTemplateChange: (document: TemplateBoxTemplate) => void;
  isDisabled?: boolean;
} & AllOrNone<{
  isWithNote: boolean;
  onNoteChange: (note: string | null) => void;
  noteLabel?: string;
}> &
  TestIdProps;

type LocaleGroup = {
  key: string;
  label: string;
  templates: TemplateBoxTemplate[];
};

const groupTemplatesByLocale = (
  templates: TemplateBoxTemplate[],
  preferredLocale: string | Nullish
): LocaleGroup[] => {
  const grouped = groupBy<TemplateBoxTemplate>(
    (template) => template.locale ?? OTHERS_LOCALE_KEY,
    templates
  );

  return toPairs(grouped)
    .map(([key, items]): LocaleGroup => {
      const baseLabel =
        key === OTHERS_LOCALE_KEY ? i18n.t('general.labels.others') : (getLocaleLabel(key) ?? key);
      const label =
        isNotNil(preferredLocale) && key === preferredLocale
          ? `${baseLabel} (${i18n.t('general.labels.preferred')})`
          : baseLabel;
      return {key, label, templates: items ?? []};
    })
    .sort((a, b) =>
      match({
        aPreferred: a.key === preferredLocale,
        bPreferred: b.key === preferredLocale,
        aOthers: a.key === OTHERS_LOCALE_KEY,
        bOthers: b.key === OTHERS_LOCALE_KEY,
      })
        .with({aPreferred: true, bPreferred: false}, () => -1)
        .with({aPreferred: false, bPreferred: true}, () => 1)
        .with({aOthers: true, bOthers: false}, () => 1)
        .with({aOthers: false, bOthers: true}, () => -1)
        .otherwise(() => a.label.localeCompare(b.label))
    );
};

const sortTemplatesByPreferredLocale = (
  templates: TemplateBoxTemplate[],
  preferredLocale: string | Nullish
): TemplateBoxTemplate[] =>
  [...templates].sort((a, b) =>
    match({
      aPreferred: a.locale === preferredLocale,
      bPreferred: b.locale === preferredLocale,
    })
      .with({aPreferred: true, bPreferred: false}, () => -1)
      .with({aPreferred: false, bPreferred: true}, () => 1)
      .otherwise(() => 0)
  );

export function DocumentTemplateBox(props: DocumentTemplateBoxProps) {
  const [note, setNote] = useState<string | null>(null);
  const {isMultiLocale} = useTenant();

  if (isNilOrEmpty(props.templates) && isNilOrEmpty(props.authorizationTemplates)) {
    return null;
  }

  const noteType = match(props.type)
    .with('purchase-brokerage-contract', () => 'brokerage-contract')
    .with('sale-brokerage-contract', () => 'brokerage-contract')
    .otherwise(always(props.type));

  const handleNoteChange = (note: string | null) => {
    setNote(note);
    props.onNoteChange?.(note);
  };

  const isAuthEnabled = isNotNilOrEmpty(props.authorizationTemplates);
  const showLocaleFlag = isMultiLocale && isAuthEnabled;
  const groupByLocale = isMultiLocale && !isAuthEnabled;

  const renderItem = (template: TemplateBoxTemplate, testId: string) => (
    <DropdownItem
      data-testid={testId}
      key={template.id}
      label={template.title}
      suffix={
        showLocaleFlag && isNotNil(template.locale) ? (
          <Flag
            size="small"
            colorScheme="neutral"
            isSubtle
            label={getLocaleLabel(template.locale) ?? template.locale}
          />
        ) : undefined
      }
      onClick={() => props.onTemplateChange(template)}
    />
  );

  const localeGroups = groupByLocale
    ? groupTemplatesByLocale(props.templates ?? [], props.preferredLocale)
    : null;

  return (
    <Card
      title={props.title}
      variant="inlineGrey"
      isExpanded={props.isWithNote}
      control={{
        type: 'checkbox',
        value: Boolean(props.isSelected),
        onChange: props.onSelect,
        isDisabled: props.isDisabled,
        'data-testid': suffixTestId('isSelected', props),
      }}
      actions={[
        {
          type: 'custom',
          component: (
            <Dropdown
              data-testid={props['data-testid']}
              isHeightLimited
              dropdownControl={
                <Button
                  wrap
                  variant="secondary"
                  rightIcon="navigation/expand_more"
                  title={props.selectedTemplate?.title || i18n.t('general.labels.template')}
                />
              }
            >
              {localeGroups ? (
                localeGroups.map((group, groupIndex) => (
                  <Fragment key={group.key}>
                    <Show when={groupIndex > 0}>
                      <Space vertical={2} />
                    </Show>
                    <Label>{group.label}</Label>
                    <Separator spacing={0} />
                    {group.templates.map((template, index) =>
                      renderItem(template, suffixTestId(`template-${group.key}[${index}]`, props))
                    )}
                  </Fragment>
                ))
              ) : (
                <>
                  <Show when={isAuthEnabled}>
                    <Label>
                      {props.authorizationProfileLabel ?? i18n.t('general.labels.template')}
                    </Label>
                    <Separator spacing={0} />
                    {sortTemplatesByPreferredLocale(
                      props.authorizationTemplates ?? [],
                      props.preferredLocale
                    ).map((template, index) =>
                      renderItem(template, suffixTestId(`auth-profile-template[${index}]`, props))
                    )}

                    <Space vertical={2} />
                    <Label>
                      {i18n.t('entity.authorizationProfiles.labels.withoutAuthorization')}
                    </Label>
                    <Separator spacing={0} />
                  </Show>

                  <Show when={isNotNilOrEmpty(props.templates)}>
                    {sortTemplatesByPreferredLocale(
                      props.templates ?? [],
                      props.preferredLocale
                    ).map((template, index) =>
                      renderItem(template, suffixTestId(`template[${index}]`, props))
                    )}
                  </Show>
                </>
              )}
            </Dropdown>
          ),
        },
      ]}
      data-testid={props['data-testid']}
    >
      {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
      {props.isWithNote && (
        <>
          <HStack align="flex-end">
            <Box flex={1}>
              <Label>{props.noteLabel || i18n.t('general.labels.note')}</Label>
            </Box>
            <Show when={isNotNilOrEmpty(noteType) ? noteType : null}>
              {(noteType) => (
                <PredefinedNotes
                  note={note}
                  onPrefill={handleNoteChange}
                  context={noteType}
                  resource="BUSINESS_CASE"
                  isLinkVariant
                  data-testid={suffixTestId('predefinedNotes', props)}
                />
              )}
            </Show>
          </HStack>
          <Textarea
            name="note"
            onChange={handleNoteChange}
            value={note}
            minRows={2}
            data-testid={suffixTestId('note', props)}
          />
        </>
      )}
    </Card>
  );
}
